import { useCallback, useEffect, useRef, useState } from 'react';
import { aiAssistantApi } from '../../services/aiAssistantApi';

export interface RealtimeFunctionCall {
  callId: string;
  name: string;
  arguments: Record<string, unknown>;
}

interface Options {
  onUserTranscript: (text: string) => void;
  onAssistantTranscript: (text: string, done: boolean) => void;
  onFunctionCall: (call: RealtimeFunctionCall) => void;
}

function parseArgs(raw: string): Record<string, unknown> {
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? v as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

export function useOpenAiRealtime(options: Options) {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [micEnabled, setMicEnabledState] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const assistantBufRef = useRef('');
  const connectingRef = useRef(false);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const sendEvent = useCallback((event: Record<string, unknown>) => {
    const dc = dcRef.current;
    if (dc && dc.readyState === 'open') {
      dc.send(JSON.stringify(event));
    }
  }, []);

  const disconnect = useCallback(() => {
    dcRef.current?.close();
    dcRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioElRef.current) {
      audioElRef.current.srcObject = null;
      audioElRef.current.remove();
      audioElRef.current = null;
    }
    connectingRef.current = false;
    setConnected(false);
    setConnecting(false);
    setMicEnabledState(false);
  }, []);

  const handleServerEvent = useCallback((raw: string) => {
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(raw);
    } catch {
      return;
    }
    const type = String(event.type ?? '');

    if (type === 'conversation.item.input_audio_transcription.completed') {
      const transcript = String(event.transcript ?? '');
      if (transcript.trim()) optionsRef.current.onUserTranscript(transcript.trim());
      return;
    }

    if (type === 'response.output_audio_transcript.delta' || type === 'response.audio_transcript.delta') {
      assistantBufRef.current += String(event.delta ?? '');
      optionsRef.current.onAssistantTranscript(assistantBufRef.current, false);
      return;
    }

    if (type === 'response.output_audio_transcript.done' || type === 'response.audio_transcript.done') {
      const text = String(event.transcript ?? assistantBufRef.current).trim();
      assistantBufRef.current = '';
      if (text) optionsRef.current.onAssistantTranscript(text, true);
      return;
    }

    if (type === 'response.done') {
      const response = event.response as { output?: Array<Record<string, unknown>> } | undefined;
      for (const item of response?.output ?? []) {
        if (item.type === 'function_call') {
          optionsRef.current.onFunctionCall({
            callId: String(item.call_id ?? ''),
            name: String(item.name ?? ''),
            arguments: parseArgs(String(item.arguments ?? '{}')),
          });
        }
      }
      return;
    }

    if (type === 'error') {
      const err = event.error as { message?: string } | undefined;
      setError(err?.message || 'Realtime error');
    }
  }, []);

  const connect = useCallback(async () => {
    if (pcRef.current || connectingRef.current) return;
    connectingRef.current = true;
    setError(null);
    setConnecting(true);
    try {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.setAttribute('playsinline', 'true');
      audioElRef.current = audioEl;
      pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
      };

      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = ms;
      const track = ms.getTracks()[0];
      track.enabled = false;
      pc.addTrack(track, ms);

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.addEventListener('message', (e) => handleServerEvent(String(e.data)));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const answerSdp = await aiAssistantApi.createRealtimeSession(offer.sdp ?? '');
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      setConnected(true);
    } catch (e) {
      disconnect();
      setError(e instanceof Error ? e.message : 'Could not start Realtime voice');
    } finally {
      connectingRef.current = false;
      setConnecting(false);
    }
  }, [disconnect, handleServerEvent]);

  const setMicEnabled = useCallback((enabled: boolean) => {
    streamRef.current?.getAudioTracks().forEach((t) => {
      t.enabled = enabled;
    });
    setMicEnabledState(enabled);
  }, []);

  const sendFunctionOutput = useCallback((callId: string, output: unknown) => {
    sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: typeof output === 'string' ? output : JSON.stringify(output),
      },
    });
    sendEvent({ type: 'response.create' });
  }, [sendEvent]);

  const sendUserText = useCallback((text: string) => {
    sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text }],
      },
    });
    sendEvent({ type: 'response.create' });
  }, [sendEvent]);

  useEffect(() => () => disconnect(), [disconnect]);

  return {
    connecting,
    connected,
    micEnabled,
    error,
    connect,
    disconnect,
    setMicEnabled,
    sendFunctionOutput,
    sendUserText,
  };
}
